// Countdown for a store on its 30 free days with no plan (review W31).
// App renders it above every main view except Billing (which shows the same
// facts in its own card), in the slot the "Showing saved data" banner uses.
// It renders nothing once the period has ended: that case belongs to the
// end-of-period picker and the Dashboard paywall banner.
//
// Days come from endsAt on this clock (daysUntil), never the server's
// daysLeft, so a cached status can't show yesterday's count.
//
//   more than 7 days  info, dismissible for the session
//   2 to 7 days       warning, not dismissible
//   last day          warning, not dismissible

import { useState } from "react";
import { Banner, BlockStack, Button, InlineStack } from "@shopify/polaris";
import { shopScopedKey } from "./api";
import { openPlanSelection, type FreePeriodView } from "./billing-shared";
import { daysUntil, formatDate } from "./dates";

const DISMISS_KEY = "dc_freeBannerDismissed";

export function FreePeriodBanner({
  freePeriod,
  planSelectionUrl,
}: {
  freePeriod: FreePeriodView;
  planSelectionUrl?: string;
}) {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(shopScopedKey(DISMISS_KEY)) === "1";
    } catch {
      return false;
    }
  });

  const days = daysUntil(freePeriod.endsAt);
  if (freePeriod.ended || days <= 0) return null;
  const date = formatDate(freePeriod.endsAt);
  const early = days > 7;
  if (early && dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(shopScopedKey(DISMISS_KEY), "1");
    } catch {
      // storage blocked: dismissed for this page view only.
    }
  };

  const title = early
    ? `${days} days of free use left`
    : days === 1
      ? "Less than a day left in your free period"
      : `${days} days left in your free period`;
  const body = early
    ? `Datacrow is free until ${date}. Pick a plan before then to keep sending events to your ad platforms.`
    : `On ${date}, Datacrow stops sending events to your ad platforms until you pick a plan. ` +
      "Events from the paused days are not sent later. Standard and Pro start with a 30-day free trial.";

  return (
    <div style={{ maxWidth: 998, margin: "16px auto 0", padding: "0 16px" }}>
      <Banner tone={early ? "info" : "warning"} title={title} onDismiss={early ? dismiss : undefined}>
        <BlockStack gap="200">
          <p>{body}</p>
          <InlineStack>
            <Button onClick={() => openPlanSelection(planSelectionUrl)} disabled={!planSelectionUrl}>
              Choose a plan
            </Button>
          </InlineStack>
        </BlockStack>
      </Banner>
    </div>
  );
}
